import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../../state/app.state';
import { CreateExternalLabRequest, UpdateExternalLabRequest } from '../../../../../../../core/projects.actions';
import { ProjectsService } from '../../../../../../../core/projects.service';
import { CommonService, EMAIL_PATTERN } from '../../../../../../../../../shared/services/common.service';

@Component({
    selector: 'app-create-or-edit-external-lab',
    templateUrl: './create-or-edit-external-lab.component.html',
    styleUrls: ['./create-or-edit-external-lab.component.scss'],
    standalone: false
})
export class CreateOrEditExternalLabComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() data: any;

  form = new FormGroup({
    name: new FormControl({ value: null, disabled: false }, [Validators.required]),
    phone: new FormControl({ value: null, disabled: false }),
    email: new FormControl(
      { value: null, disabled: false },
      [Validators.pattern(EMAIL_PATTERN)]
    ),
    contacts: new FormArray([]),
  });

  errorMessages: any = {};
  errorMessageMap = {
    name: {
      required: 'This field is required.',
    },
    email: {
      pattern: 'Please enter a valid email address.',
    },
  };
  contactErrorMessages: any[] = [];
  isFormDirty = false;
  projectId: string | null = null;
  private readonly onDestroy = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private router: Router,
    private projectsService: ProjectsService,
    private commonService: CommonService
  ) {}

  get contacts(): FormArray {
    return this.form.get('contacts') as FormArray;
  }

  getContactGroup(index: number): FormGroup {
    return this.contacts.at(index) as FormGroup;
  }

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
    } else {
      const url = this.router.url;
      const path = url.split('?')[0];
      const segments = path.split('/');
      this.projectId = segments[segments.length - 1];
    }

    if (!this.projectId && this.data?.projectId) {
      this.projectId = this.data?.projectId;
    }

    if (this.data?.id || this.data?.uuid || this.data?.name || this.data?.value) {
      this.form.patchValue({
        name: this.data?.name || this.data?.value || null,
        phone: this.data?.phone || null,
        email: this.data?.email || null,
      });

      // Load existing contacts
      if (this.data?.contacts?.length) {
        for (const contact of this.data.contacts) {
          this.addContact(contact);
        }
      }
    }

    // If no contacts exist, add one empty row
    if (this.contacts.length === 0) {
      this.addContact();
    }

    this.projectsService.isAnyFormDirty.pipe(takeUntil(this.onDestroy)).subscribe(dirty => {
      this.isFormDirty = dirty;
    });

    this.form.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.isFormDirty = true;
      this.projectsService.isAnyFormDirty.next(true);
    });
  }

  ngAfterViewInit(): void {
    this.projectsService.isAnyFormDirty.next(false);
  }

  addContact(contact?: any): void {
    const group = new FormGroup({
      id: new FormControl(contact?.id || null),
      name: new FormControl(contact?.name || null, [Validators.required]),
      phone: new FormControl(contact?.phone || null, [Validators.required]),
      email: new FormControl(contact?.email || null, [Validators.required, Validators.pattern(EMAIL_PATTERN)]),
    });
    this.contacts.push(group);
    this.contactErrorMessages.push({});
  }

  removeContact(index: number): void {
    this.contacts.removeAt(index);
    this.contactErrorMessages.splice(index, 1);
    this.isFormDirty = true;
    this.projectsService.isAnyFormDirty.next(true);
  }

  submit(): void {
    this.form.updateValueAndValidity();
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.checkForErrors();
      this.checkContactErrors();
      return;
    }

    if (!this.projectId) return;

    const values = this.form.getRawValue() as any;
    const contactsPayload = (values.contacts || []).map((c: any) => ({
      ...(c.id && { id: c.id }),
      name: c.name,
      phone: c.phone ?? undefined,
      email: c.email ?? undefined,
    }));

    const payload = {
      projectId: this.projectId,
      body: {
        name: values?.name,
        phone: values?.phone ?? undefined,
        email: values?.email ?? undefined,
        contacts: contactsPayload,
        project_id: this.projectId,
        ...(this.data?.id && { id: this.data.id }),
      },
    };
    if (this.data?.id) {
      this.store.dispatch(UpdateExternalLabRequest({ payload }));
    } else {
      this.store.dispatch(CreateExternalLabRequest({ payload }));
    }
  }

  checkForErrors(currentField?: string): void {
    const validationErrors = this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField);
    if (validationErrors) {
      this.errorMessages = {
        ...this.errorMessages,
        ...validationErrors,
      };
    }
  }

  checkContactErrors(): void {
    this.contacts.controls.forEach((control, i: number) => {
      const group = control as FormGroup;
      const errors: any = {};
      if (group.get('name')?.errors?.required) {
        errors.name = 'Name is required.';
      }
      if (group.get('phone')?.errors?.required) {
        errors.phone = 'Phone is required.';
      }
      if (group.get('email')?.errors?.required) {
        errors.email = 'Email is required.';
      } else if (group.get('email')?.errors?.pattern) {
        errors.email = 'Please enter a valid email address.';
      }
      this.contactErrorMessages[i] = errors;
    });
  }

  ngOnDestroy(): void {
    this.projectsService.isAnyFormDirty.next(false);
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
